import { toast } from "react-toastify";
import {
  useCreateMutation,
  useDestroyMutation,
  useIndexQuery,
  useShowQuery,
  useUpdateMutation,
} from "../api/genericHooks";
import i18n from "../i18n";
import {
  AnecdoteWithChapterResponse,
  CreateAnecdoteRequest,
} from "../types/api/anecdotes";
import { Anecdote, AnecdoteStatus } from "../types/models";

/**
 * Hook pour récupérer la liste des anecdotes (filtrée)
 */
export const useAnecdotesList = (
  book_id?: number,
  chapter_number?: number,
  generated_chapter_id?: number,
  status?: AnecdoteStatus
) => {
  return useIndexQuery(
    "/anecdotes",
    { book_id, chapter_number, generated_chapter_id, status },
    {
      enabled: !!book_id || !!chapter_number || !!generated_chapter_id,
      staleTime: 1000 * 60 * 5, // Fresh for 5 minutes
    }
  );
};

/**
 * Hook pour récupérer un témoignage par ID
 */
export const useAnecdote = (id: number | undefined | null) => {
  return useShowQuery(
    {
      route: "/anecdotes/:id",
      pathParams: { id: id || 0 },
    },
    id
  );
};

/**
 * Hook pour récupérer un témoignage par token (public)
 */
export const useAnecdoteByToken = (token: string) => {
  return useIndexQuery(
    "/anecdotes/show_by_token",
    { token },
    {
      enabled: !!token,
    }
  ) as {
    data: AnecdoteWithChapterResponse | undefined;
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
  };
};

/**
 * Hook pour créer une ou plusieurs demandes de témoignages
 */
export const useCreateAnecdotes = () => {
  return useCreateMutation("/anecdotes", {
    onSuccess: (data: Anecdote[]) => {
      const count = data.length;
      if (count === 1) {
        toast.success(i18n.t("anecdotes.toast.requestSentSingle"));
      } else {
        toast.success(i18n.t("anecdotes.toast.requestSentMultiple", { count }));
      }
    },
    onError: () => {
      toast.error(i18n.t("anecdotes.toast.requestError"));
    },
  });
};

/**
 * Hook pour soumettre un témoignage (public, avec photo possible)
 */
export const useSubmitAnecdote = () => {
  return useCreateMutation(
    "/anecdotes/submit",
    {
      onSuccess: () => {
        toast.success(i18n.t("anecdotes.toast.submitSuccess"));
      },
      onError: () => {
        toast.error(i18n.t("anecdotes.toast.submitError"));
      },
    },
    false, // needsSerialization = false car FormData
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
};

/**
 * Hook pour accepter un témoignage
 */
export const useAcceptAnecdote = (id: number) => {
  return useUpdateMutation(
    {
      route: "/anecdotes/:id/accept",
      pathParams: { id },
    },
    {
      onSuccess: () => {
        toast.success(i18n.t("anecdotes.toast.acceptSuccess"));
      },
      onError: () => {
        toast.error(i18n.t("anecdotes.toast.acceptError"));
      },
    }
  );
};

/**
 * Hook pour rejeter un témoignage
 */
export const useRejectAnecdote = (id: number) => {
  return useUpdateMutation(
    {
      route: "/anecdotes/:id/reject",
      pathParams: { id },
    },
    {
      onSuccess: () => {
        toast.success(i18n.t("anecdotes.toast.rejectSuccess"));
      },
      onError: () => {
        toast.error(i18n.t("anecdotes.toast.rejectError"));
      },
    }
  );
};

/**
 * Hook pour renvoyer l'email de demande de témoignage
 */
export const useResendAnecdote = (id: number) => {
  return useCreateMutation(
    {
      route: "/anecdotes/:id/resend",
      pathParams: { id },
    },
    {
      onSuccess: () => {
        toast.success(i18n.t("anecdotes.toast.resendSuccess"));
      },
      onError: () => {
        toast.error(i18n.t("anecdotes.toast.resendError"));
      },
    }
  );
};

/**
 * Hook pour supprimer une demande de témoignage
 */
export const useDeleteAnecdote = (id: number) => {
  return useDestroyMutation(
    {
      route: "/anecdotes/:id",
      pathParams: { id: id },
    },
    {
      onSuccess: () => {
        toast.success(i18n.t("anecdotes.toast.deleteSuccess"));
      },
      onError: () => {
        toast.error(i18n.t("anecdotes.toast.deleteError"));
      },
    }
  );
};

/**
 * Hook pour masquer le bandeau/tooltip de proposition de témoignages
 */
export const useDismissAnecdoteBanner = () => {
  return useUpdateMutation("/users/dismiss_anecdote_banner", {
    onSuccess: () => {
      // Pas de toast, action silencieuse
    },
    onError: () => {
      toast.error(i18n.t("anecdotes.toast.updateError"));
    },
  });
};

/**
 * Helper pour créer les paramètres FormData pour la soumission de témoignage
 */
export const createSubmitAnecdoteFormData = (
  token: string,
  content: string,
  marketing_accepted: boolean,
  photo?: File
): FormData => {
  const formData = new FormData();
  formData.append("token", token);
  formData.append("content", content);
  formData.append("marketing_accepted", String(marketing_accepted));
  if (photo) {
    formData.append("photo", photo);
  }
  return formData;
};

/**
 * Helper pour créer les paramètres de création de témoignages (batch)
 */
export const createAnecdotesParams = (
  requests: CreateAnecdoteRequest[]
): { anecdotes: CreateAnecdoteRequest[] } => {
  return { anecdotes: requests };
};
